<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px;font-size: small;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #FFF8F0;">
          <h5 class="card-title">Gestión de Categorías</h5>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Buscar por nombre o descripción..."
                    @keyup.enter="handleSearch"
                  />
                  <button 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <BButton
                v-if="hasPermission('param_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nueva Categoría
              </BButton>
              <BButton
                v-if="hasPermission('param_create')"
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>
          
          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Partida Presupuestaria</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="categoria in categorias" :key="categoria.id">
                    <td>{{ categoria.nombre_categoria }}</td>
                    <td>{{ categoria.descripcion }}</td>
                    <td>{{ categoria.id_partida }}: {{ categoria.partidas[0]?.nombre }}</td>
                    <td>
                      <div class="btn-group">
                        <BButton
                          v-if="hasPermission('param_update')"
                          @click="showModal(categoria)"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton
                          v-if="hasPermission('param_delete')"
                          @click="confirmDelete(categoria)"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Paginación -->
            <div class="row mt-3" v-if="pagination.total > 0">
              <div class="col-md-12">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  @input="handlePageChange"
                  align="center"
                ></BPagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Nombre de la Categoría</label>
              <input
                type="text"
                v-model="form.nombre_categoria"
                class="form-control"
                :class="{ 'is-invalid': errors.nombre_categoria }"
                required
                maxlength="100"
              />
              <div class="invalid-feedback" v-if="errors.nombre_categoria">
                {{ errors.nombre_categoria[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Partida Presupuestaria</label>
              <select
                v-model="form.id_partida"
                class="form-select"
                :class="{ 'is-invalid': errors.id_partida }"
                required
              >
                <option value="">Seleccione una partida</option>
                <option 
                  v-for="partida in partidas" 
                  :key="partida.codigo" 
                  :value="partida.codigo"
                >
                  {{ partida.codigo }}:{{ partida.nombre }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_partida">
                {{ errors.id_partida[0] }}
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Descripción</label>
              <textarea
                v-model="form.descripcion"
                class="form-control"
                :class="{ 'is-invalid': errors.descripcion }"
                rows="3"
              ></textarea>
              <div class="invalid-feedback" v-if="errors.descripcion">
                {{ errors.descripcion[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination } from 'bootstrap-vue-3';

interface Categoria {
  id: number;
  nombre_categoria: string;
  descripcion: string | null;
  id_partida: string; 
}

interface Filters {
  search: string;
}

interface FormData {
  id: number | null;
  nombre_categoria: string;
  descripcion: string;
  id_partida: string;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default {
  components: {
    BModal,
    BButton,
    BPagination
  },
  setup() {
    const categorias = ref<Categoria[]>([]);
    const partidas = ref([]);
    const loading = ref(true);
    const authStore = useAuthStore();
    const filters = ref<Filters>({
      search: ''
    });
    
    // Variables para el modal
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const loadingForm = ref(false);
    const errors = ref<Record<string, string[]>>({});

    const form = ref<FormData>({
      id: null,
      nombre_categoria: '',
      descripcion: '',
      id_partida: '',
    });

    // Paginación
    const pagination = ref<Pagination>({
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    });

    const modalTitle = computed(() => {
      return isEditing.value ? 'Editar Categoría' : 'Nueva Categoría';
    });

    const showModal = (categoria: Categoria | null) => {
      errors.value = {};
      if (categoria) {
        // Edición
        isEditing.value = true;
        form.value = {
          id: categoria.id,
          nombre_categoria: categoria.nombre_categoria,
          descripcion: categoria.descripcion || '',
          id_partida: categoria.id_partida
        };
      } else {
        // Nuevo
        isEditing.value = false;
        form.value = {
          id: null,
          nombre_categoria: '',
          descripcion: '',
          id_partida: ''
        };
      }
      modalVisible.value = true;
    };

    const handleSubmit = async () => {
      loadingForm.value = true;
      errors.value = {};
      
      try {
        let response;
        
        if (isEditing.value && form.value.id) {
          response = await axios.post(`/categoria/${form.value.id}/update`, form.value);
        } else {
          response = await axios.post('/categoria/save', form.value);
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: isEditing.value ? 'Categoría actualizada correctamente' : 'Categoría creada correctamente'
        });
        
        modalVisible.value = false;
        fetchCategorias();
      } catch (error: any) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Ocurrió un error al procesar la solicitud'
          });
        }
      } finally {
        loadingForm.value = false;
      }
    };

    const fetchCategorias = async () => {
      loading.value = true;
      try {
        const params: Record<string, any> = {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page
        };
        
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        
        const response = await axios.get('/categoria/list', { params });
        categorias.value = response.data.categorias.data;
        
        // Actualizar paginación
        pagination.value = {
          current_page: response.data.categorias.current_page,
          per_page: response.data.categorias.per_page,
          total: response.data.categorias.total,
          last_page: response.data.categorias.last_page
        };
        
      } catch (error) {
        console.error('Error al cargar categorías:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las categorías'
        });
      } finally {
        loading.value = false;
      }
    };

    const fetchPartidas = async () => {
      try {
        const response = await axios.get('/compras/partidas');
        partidas.value = response.data.data;
      } catch (error) {
        console.error('Error al cargar partidas:', error);
      }
    };
    const handleSearch = () => {
      pagination.value.current_page = 1;
      fetchCategorias();
    };

    const handlePageChange = (page: number) => {
      pagination.value.current_page = page;
      fetchCategorias();
    };
    
    const confirmDelete = (categoria: Categoria) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar la categoría "${categoria.nombre_categoria}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteCategoria(categoria.id);
        }
      });
    };
    
    const deleteCategoria = async (id: number) => {
      try {
        await axios.get(`/categoria/${id}/delete`);
        Swal.fire({
          icon: 'success',
          title: 'Categoría eliminada',
          text: 'La categoría ha sido eliminada correctamente'
        });
        await fetchCategorias();
      } catch (error: any) {
        console.error('Error al eliminar categoría:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar la categoría'
        });
      }
    };

    const generatePDF = () => {
      const params = filters.value.search ? { search: filters.value.search } : {};
      
      axios.get('/categoria/reporte', {
        params,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_categorias.pdf`);
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }).catch(error => {
        console.error('Error al generar PDF:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF'
        });
      });
    };
    
    const hasPermission = (permission: string) => {
      return authStore.hasPermission(permission);
    };
    
    onMounted(() => {
      fetchCategorias();
      fetchPartidas();
    });
    
    return {
      categorias,
      partidas,
      filters,
      loading,
      form,
      errors,
      loadingForm,
      isEditing,
      modalTitle,
      modalVisible,
      pagination,
      handleSearch,
      handlePageChange,
      confirmDelete,
      hasPermission,
      showModal,
      handleSubmit,
      generatePDF
    };
  }
};
</script>

<style scoped>
  thead th {
    background-color: #D84315;
    color:#ffffff;
    position: sticky;
    top: 0;
  }
  tbody td {
    margin: 0px;
    padding: 8px !important;
    vertical-align: middle;
  }
  .table-responsive {
    max-height: 600px;
    overflow-y: auto;
  }
  .btn-round {
    border-radius: 50px;
  }
</style>